/**
 * Чат — пульт для фильтров дашборда.
 *
 * Реплика идёт двумя запросами. Первый возвращает изменение фильтров, и экран
 * перерисовывается сразу; второй дописывает текст, пока карточки уже считают.
 * Слитые в один, они заставили бы ждать формулировку, чтобы увидеть таблицу.
 */

import { useState } from "react";

import { ApiError, parseMessage, requestReply } from "../api/client";
import type { Conflict, FilterState } from "../api/types";
import { METRIC_LABELS, periodLabel, ROLE_LABELS } from "../state/filters";
import { useStore } from "../state/store";

type Message = {
  id: number;
  role: "user" | "system";
  text: string;
  changes?: string[];
  conflicts?: Conflict[];
  source?: "model" | "template";
  pending?: boolean;
};

let counter = 0;
const nextId = () => (counter += 1);

export function Chat() {
  const { state, tool, replace, setTool, patch } = useStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const append = (message: Omit<Message, "id">) => {
    const id = nextId();
    setMessages((current) => [...current, { ...message, id }]);
    return id;
  };

  const update = (id: number, changes: Partial<Message>) => {
    setMessages((current) =>
      current.map((message) => (message.id === id ? { ...message, ...changes } : message)),
    );
  };

  async function send() {
    const message = draft.trim();
    if (!message || busy) return;
    setDraft("");
    setBusy(true);
    append({ role: "user", text: message });

    try {
      const parsed = await parseMessage(message, state, tool);

      if (!parsed.understood) {
        // Непонятая реплика ничего не меняет. Молча оставить прежний экран
        // значило бы выдать старый ответ за ответ на новый вопрос.
        append({ role: "system", text: "Не понял реплику — фильтры оставил как были." });
        setBusy(false);
        return;
      }

      const { resolved, ...next } = parsed.state;
      void resolved;
      replace(next as FilterState);
      setTool(parsed.primary_tool);

      if (parsed.conflicts.length > 0) {
        append({
          role: "system",
          text: "Уточните, кого имеете в виду.",
          conflicts: parsed.conflicts,
          changes: describe(parsed.applied),
        });
        setBusy(false);
        return;
      }

      if (parsed.missing.slots.length > 0) {
        append({
          role: "system",
          text: parsed.missing.message,
          changes: describe(parsed.applied),
        });
        setBusy(false);
        return;
      }

      const placeholder = append({
        role: "system",
        text: "Считаю…",
        changes: describe(parsed.applied),
        pending: true,
      });

      const reply = await requestReply(message, next as FilterState, parsed.primary_tool);
      if (reply.status === "ok") {
        update(placeholder, { text: reply.text, source: reply.source, pending: false });
      } else if (reply.status === "forbidden") {
        update(placeholder, { text: reply.message, pending: false });
      } else {
        update(placeholder, {
          text: reply.missing?.message ?? "Не хватает данных для ответа.",
          conflicts: reply.conflicts,
          pending: false,
        });
      }
    } catch (cause: unknown) {
      append({
        role: "system",
        text: cause instanceof ApiError ? cause.message : "Не удалось обработать реплику.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="chat">
      <header className="chat__head">
        <h2>Спросить словами</h2>
        <p className="panel__hint">Реплика меняет фильтры слева. «А за февраль?» тоже работает.</p>
      </header>

      <div className="chat__log">
        {messages.length === 0 && (
          <p className="muted">
            Например: «Сколько выпусков провела Муратова за второй квартал 2026?»
          </p>
        )}
        {messages.map((message) => (
          <div className={`bubble bubble--${message.role}`} key={message.id}>
            {message.changes && message.changes.length > 0 && (
              <ul className="bubble__changes">
                {message.changes.map((change) => (
                  <li key={change}>{change}</li>
                ))}
              </ul>
            )}
            <p className={message.pending ? "bubble__text muted" : "bubble__text"}>{message.text}</p>
            {message.conflicts?.map((conflict) => (
              <div className="needs__choices" key={conflict.field + conflict.query}>
                {conflict.candidates.map((candidate) => (
                  <button
                    type="button"
                    className="button button--ghost"
                    key={candidate}
                    onClick={() => patch({ [conflict.field]: candidate } as Partial<FilterState>)}
                  >
                    {candidate}
                  </button>
                ))}
              </div>
            ))}
            {/* Шаблон означает, что модель либо недоступна, либо употребила
                число не из расчёта. Числа при этом верны в обоих случаях,
                поэтому помечается только необычный случай. */}
            {message.source === "template" && (
              <p className="bubble__note">Формулировка шаблонная: модель не написала текст.</p>
            )}
          </div>
        ))}
      </div>

      <form
        className="chat__form"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <input
          className="input"
          value={draft}
          placeholder="Спросите о статистике"
          onChange={(event) => setDraft(event.target.value)}
          disabled={busy}
        />
        <button type="submit" className="button" disabled={busy || !draft.trim()}>
          {busy ? "…" : "Спросить"}
        </button>
      </form>
    </aside>
  );
}

/** Человеческое описание патча: «период → 2 квартал 2026». */
function describe(applied: Record<string, unknown>): string[] {
  return Object.entries(applied).map(([key, value]) => {
    switch (key) {
      case "period":
        return `период → ${periodLabel(value as never)}`;
      case "person":
        return value ? `человек → ${String(value)}` : "фильтр по человеку снят";
      case "person_b":
        return value ? `второй человек → ${String(value)}` : "второй человек убран";
      case "program":
        return value ? `программа → ${String(value)}` : "фильтр по программе снят";
      case "metric":
        return `метрика → ${METRIC_LABELS[value as keyof typeof METRIC_LABELS] ?? String(value)}`;
      case "role":
        return `роль → ${ROLE_LABELS[value as keyof typeof ROLE_LABELS] ?? String(value)}`;
      case "granularity":
        return `шаг графика → ${String(value)}`;
      case "tool":
        return `отчёт → ${String(value)}`;
      default:
        return `${key} → ${String(value)}`;
    }
  });
}
